<component-modal [showFooter]="false">
  <ng-container title>
    <div class="d-flex align-items-center justify-content-between w-100">
      <span>Share document</span>
      <button type="button" class="btn btn-sm btn-link text-decoration-none d-flex align-items-center gap-2" (click)="copyLink()">
        <lucide-icon [img]="linkCopied() ? checkIcon : linkIcon" size="16"></lucide-icon>
        {{ linkCopied() ? "Link copied" : "Copy link" }}
      </button>
    </div>
  </ng-container>

  <ng-container body>
    <div class="d-flex flex-column gap-3">
      <!-- Form Section -->
      <form class="d-flex flex-column gap-3">
        <!-- Invite Row -->
        <div class="form-group">
          <label class="form-label" for="typeahead-user">Email or username</label>
          <div class="d-flex gap-2">
            <div class="flex-grow-1 position-relative">
              <input
                id="typeahead-user"
                #instance="ngbTypeahead"
                type="text"
                class="form-control form-control-lg"
                [ngModel]="formState().recipient"
                (ngModelChange)="updateRecipient($event)"
                [ngModelOptions]="{ standalone: true }"
                [ngbTypeahead]="searchUsers"
                [inputFormatter]="formatter"
                [resultFormatter]="formatter"
                [selectOnExact]="true"
                (selectItem)="onUserSelected($event)"
                (focus)="focus$.next($any($event).target.value)"
                (click)="click$.next($any($event).target.value)"
                [editable]="true"
                placeholder="user@example.com or username" />
            </div>

            <component-select
              class="permission-selector"
              [options]="permissionOptions"
              [value]="formState().permission"
              (singleSelectionChange)="updatePermission($event!)"
              [clearable]="false"
              [searchable]="false"
              size="md" />

            <button
              type="button"
              class="btn btn-primary"
              (click)="shareDocument()"
              [disabled]="loading() || !formState().selectedUser || isAlreadyShared()">
              Share
            </button>
          </div>
          @if (isAlreadyShared()) {
            <div class="text-danger small mt-1">This user already has access to this document</div>
          }
        </div>
      </form>

      <!-- Who has access -->
      <div class="form-group">
        <label class="form-label">Who has access</label>
        <div class="access-list border rounded-2">
          @for (s of userShares(); track s.share.id) {
            <div
              class="d-flex align-items-center justify-content-between px-3 py-2"
              [class.opacity-50]="(s.recipient && isInherited(s.recipient)) ?? false">
              <div class="d-flex align-items-center gap-3">
                <component-user-avatar [user]="s.recipient" [size]="32" />
                <div class="d-flex flex-column justify-content-center">
                  <span class="fw-medium text-sm">
                    {{ s.recipient?.recipientDisplayName }}
                  </span>
                  @if (s.recipient && isExternalUser(s.recipient)) {
                    <span class="text-xs text-muted" style="margin-top: -2px">
                      Not from organization {{ user()?.organization?.name }}
                    </span>
                  }
                </div>
              </div>

              @if (s.share.ownerId !== s.recipient?.id) {
                <div
                  class="d-flex align-items-center gap-2"
                  [title]="s.recipient && isInherited(s.recipient) ? 'Share is inherited from organization\'s share' : ''">
                  <component-select
                    [options]="permissionOptions"
                    [value]="
                      s.recipient && isInherited(s.recipient)
                        ? getPermissionOption(orgShare()?.share?.permission || formState().permission.value!) || null
                        : getPermissionOption(s.share.permission) || null
                    "
                    [disabled]="(s.recipient && isInherited(s.recipient)) ?? false"
                    [clearable]="false"
                    [searchable]="false"
                    (singleSelectionChange)="changePermission(s.share, $event)"
                    size="sm" />
                  <button type="button" class="btn btn-icon btn-sm text-tertiary" (click)="deleteShare(s.share)">
                    <custom-icon name="trashBin" [size]="18" viewBox="0 0 18 18" />
                  </button>
                </div>
              } @else {
                <span class="text-muted small">Can edit</span>
              }
            </div>
          }
          @if (userShares().length === 0) {
            <div class="list-group-item text-muted text-center py-3 small">No individual shares</div>
          }
        </div>
      </div>

      <!-- Bottom Toggles -->
      <div class="d-flex flex-column gap-2 mt-2">
        <!-- Share with Organization -->
        @if (isOrgAdmin()) {
          <div class="d-flex align-items-center justify-content-between p-3">
            <div class="d-flex align-items-center gap-3">
              <div
                class="p-2 bg-secondary-subtle d-flex align-items-center justify-content-center rounded"
                style="width: 40px; height: 40px">
                <lucide-icon [img]="usersIcon" size="20" class="text-secondary"></lucide-icon>
              </div>
              <div>
                <div class="fw-semibold text-sm">
                  Share with organization {{ orgShare()?.organization?.name || user()?.organization?.name }}
                </div>
                <div class="small text-muted">Visible to all authorized users in your domain</div>
              </div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <component-select
                [options]="permissionOptions"
                [value]="
                  getPermissionOption(orgShare()?.share?.permission || formState().permission.value!) || permissionOptions[0]
                "
                [clearable]="false"
                [searchable]="false"
                (singleSelectionChange)="changeOrgPermission($event!)"
                [disabled]="!hasOrgShare()"
                size="sm" />
              <component-toggle [checked]="hasOrgShare()" (checkedChange)="toggleShareWithOrganization()"></component-toggle>
            </div>
          </div>
        }

        <!-- Public Access -->
        <div class="d-flex align-items-center justify-content-between p-3">
          <div class="d-flex align-items-center gap-3">
            <div
              class="p-2 bg-secondary-subtle d-flex align-items-center justify-content-center rounded-2"
              style="width: 40px; height: 40px">
              <lucide-icon [img]="globeIcon" size="20" class="text-secondary"></lucide-icon>
            </div>
            <div>
              <div class="fw-semibold text-sm">Make Public (Unrestricted)</div>
              <div class="small text-muted">Visible to anyone on the internet with the link</div>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            @if (isPublic()) {
              <button
                type="button"
                class="btn btn-sm btn-link text-decoration-none d-flex align-items-center gap-2"
                (click)="copyPublicLink()">
                <lucide-icon [img]="publicLinkCopied() ? checkIcon : linkIcon" size="16"></lucide-icon>
                {{ publicLinkCopied() ? "Link copied" : "Copy public link" }}
              </button>
            }
            <component-toggle [checked]="isPublic()" (checkedChange)="togglePublic($event)"></component-toggle>
          </div>
        </div>
      </div>
    </div>
  </ng-container>
</component-modal>
